import { useState } from "react";
import { useHistory } from "react-router-dom";
import { useTheme } from "styled-components";
import { useSetRecoilState } from "recoil";
import Countdown from "react-countdown";
import { FiArrowRight, FiUsers, FiPlayCircle } from "react-icons/fi";

import { formatNumber } from "MVVM/Utilities/Clip";
import { Tabs } from "MVVM/Components/Tabs";
import { ClipFullModalState } from "MVVM/State/modals";
import { ModalType } from "MVVM/@types";
import { CountDownRenderer } from "MVVM/Components/CountDownRenderer";
import { CompactClipCard } from "./CompactClipCard";
import { CompactLeaderboard } from "./CompactLeaderboard";
import { CompactComments } from "./CompactComments";
import { ICompetition } from "../../../Competitions/@types";
import {
  COMPETITION_PLACEHOLDER_BANNER,
  getCompetitionPhase,
  getPrizeLabel,
} from "../../../Competitions/utils";
import { FeaturedCompetitionViewModel } from "./ViewModel";
import {
  TakeoverBand,
  Hero,
  ContentColumn,
  TakeoverTitle,
  LiveBadge,
  LiveDot,
  StatRow,
  StatPill,
  StatNum,
  PrizeRow,
  PrizeLabel,
  PrizeText,
  CountdownBlock,
  CountdownLabel,
  EnterButton,
  ClipsMiddle,
  HeroPanel,
  ColumnLabel,
  ClipsRail,
  TabsWrap,
  TabBody,
} from "./styledComponents";

const TABS = [
  {
    component: <></>,
    name: "leaderboard",
    title: "Leaderboard",
    type: "Leaderboard",
  },
  { component: <></>, name: "comments", title: "Comments", type: "Comments" },
];

interface FeaturedCompetitionProps {
  competition: ICompetition;
}

export const FeaturedCompetition = ({
  competition,
}: FeaturedCompetitionProps) => {
  const history = useHistory();
  const theme = useTheme() as { colors: Record<string, string> };
  const { submissions, leaderboard, leaderboardLoading } =
    FeaturedCompetitionViewModel(competition._id);
  const [activeTab, setActiveTab] = useState(0);
  const setClipModal = useSetRecoilState(ClipFullModalState);

  const openClip = (shareId: string) => {
    setClipModal({
      isOpen: ModalType.Clip,
      shareId,
      orientation: "LANDSCAPE",
      contextOverride: { type: "default", metadata: { source: "modal" } },
    });
  };

  const phase = getCompetitionPhase(competition);
  const isUpcoming = phase === "upcoming";
  const isActive = phase === "active";
  const prizeLabel = getPrizeLabel(competition.prizes);

  const { participantCount, totalSubmissions } = competition;
  const hasStats =
    typeof participantCount === "number" &&
    typeof totalSubmissions === "number";

  const accent = isUpcoming ? theme.colors.caution : theme.colors.envy;

  const banner = competition.bannerGraphic || COMPETITION_PLACEHOLDER_BANNER;
  const detailHref = `/competitions/${competition._id}`;

  const countdownTarget = new Date(
    isUpcoming ? competition.startsAt : competition.endsAt,
  );

  const handleCtaClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    history.push(isUpcoming ? detailHref : `${detailHref}/enter`);
  };

  return (
    <TakeoverBand>
      <Hero image={banner} onClick={() => history.push(detailHref)}>
        <ContentColumn>
          {isActive && (
            <LiveBadge>
              <LiveDot />
              Live now
            </LiveBadge>
          )}
          <TakeoverTitle>{competition.title}</TakeoverTitle>
          <PrizeRow>
            <div>
              <PrizeLabel>Grand prize</PrizeLabel>
              <PrizeText>{prizeLabel}</PrizeText>
            </div>
          </PrizeRow>
          {hasStats && (
            <StatRow>
              <StatPill>
                <FiUsers size={14} />
                <StatNum>{formatNumber(participantCount)}</StatNum>
                Participants
              </StatPill>
              <StatPill>
                <FiPlayCircle size={14} />
                <StatNum>{formatNumber(totalSubmissions)}</StatNum>
                Clips
              </StatPill>
            </StatRow>
          )}
          <CountdownBlock>
            <CountdownLabel accent={accent}>
              {isUpcoming ? "Starts in" : "Ends in"}
            </CountdownLabel>
            <Countdown
              date={countdownTarget}
              renderer={({ days, hours, minutes, seconds }) => (
                <CountDownRenderer
                  days={days}
                  hours={hours}
                  minutes={minutes}
                  seconds={seconds}
                  color={accent}
                  showEndsIn={false}
                  variant="text.headingSmall"
                />
              )}
            />
          </CountdownBlock>
          <EnterButton upcoming={isUpcoming} onClick={handleCtaClick}>
            {isUpcoming ? "Get notified" : "Enter competition"}
            <FiArrowRight size={16} />
          </EnterButton>
        </ContentColumn>

        {submissions.length > 0 && (
          <ClipsMiddle>
            <ColumnLabel>Recent clips</ColumnLabel>
            <ClipsRail>
              {submissions.map((submission) => (
                <CompactClipCard
                  key={submission.id}
                  clip={submission.clip}
                  submittedAt={submission.submittedAt}
                  onClick={() => openClip(submission.clip.shareId)}
                />
              ))}
            </ClipsRail>
          </ClipsMiddle>
        )}

        <HeroPanel onClick={(e) => e.stopPropagation()}>
          <TabsWrap>
            <Tabs tabs={TABS} active={activeTab} onTabChange={setActiveTab} />
          </TabsWrap>
          <TabBody>
            {activeTab === 0 ? (
              <CompactLeaderboard
                entries={leaderboard}
                loading={leaderboardLoading}
                competitionId={competition._id}
              />
            ) : (
              <CompactComments competitionId={competition._id} />
            )}
          </TabBody>
        </HeroPanel>
      </Hero>
    </TakeoverBand>
  );
};
